import { useEffect, useRef, useState } from "react";
import AppShell from "@/components/AppShell";
import { api, inr, formatErr } from "@/lib/api";
import { toast } from "sonner";
import { ScanLine, Search, Plus, Upload, X, Edit3 } from "lucide-react";

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);
  const [addUser, setAddUser] = useState(null); // null or {name,email,role,...}
  const bulkRef = useRef(null);

  const load = () => api.get("/admin/users").then(r => setUsers(r.data));
  useEffect(() => { load(); }, []);

  const filtered = users.filter(u => (filter === "all" || u.role === filter) && (q === "" || u.name.toLowerCase().includes(q.toLowerCase()) || u.email.toLowerCase().includes(q.toLowerCase())));
  
  // Get parents list for dropdown when adding a student
  const parents = users.filter(u => u.role === "parent");
  // Get students list for dropdown when adding a parent
  const students = users.filter(u => u.role === "student");

  const saveEditUser = async () => {
    try {
      const payload = {
        name: editing.name,
        phone: editing.phone,
        grade: editing.grade,
        student_id: editing.student_id,
        rfid_card_id: editing.rfid_card_id,
        parent_id: editing.parent_id || null,
        children_ids: editing.children_ids || [],
      };
      await api.patch(`/admin/users/${editing.id}`, payload);
      
      // If this is a student and parent was set, also update parent's children_ids
      if (editing.role === "student" && editing.parent_id) {
        const parent = parents.find(p => p.id === editing.parent_id);
        if (parent) {
          const newChildrenIds = [...new Set([...(parent.children_ids || []), editing.id])];
          await api.patch(`/admin/users/${editing.parent_id}`, { children_ids: newChildrenIds });
        }
      }
      
      toast.success("User updated");
      setEditing(null); load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const saveNewUser = async () => {
    if (!addUser?.name || !addUser?.email) return toast.error("Name and email required");
    try {
      const payload = {
        name: addUser.name,
        email: addUser.email,
        role: addUser.role,
        password: "grubpe123",
        grade: addUser.grade || "",
        phone: addUser.phone || "",
        rfid_card_id: addUser.rfid_card_id || "",
        student_id: addUser.student_id || "",
        parent_id: addUser.parent_id || null,
      };
      const { data } = await api.post("/auth/register", payload);
      
      // If parent with children selected, link the children
      if (addUser.role === "parent" && addUser.children_ids?.length > 0 && data.user?.id) {
        for (const childId of addUser.children_ids) {
          try {
            await api.patch(`/admin/users/${childId}`, { parent_id: data.user.id });
          } catch (e) { console.error("Failed to link child", e); }
        }
      }
      
      toast.success(`User ${addUser.name} added`);
      setAddUser(null); load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const handleBulkUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const text = await file.text();
      const lines = text.split("\n").map(l => l.trim()).filter(Boolean);
      if (lines.length < 2) return toast.error("CSV needs header + data");
      const headers = lines[0].split(",").map(h => h.trim().toLowerCase().replace(/[^a-z_]/g, ""));
      let success = 0, errors = 0;
      for (let i = 1; i < lines.length; i++) {
        const cols = lines[i].split(",").map(c => c.trim().replace(/^"|"$/g, ""));
        const row = {}; headers.forEach((h, idx) => { row[h] = cols[idx] || ""; });
        try {
          await api.post("/auth/register", { name: row.name, email: row.email, role: row.role || "student", grade: row.grade || "", phone: row.phone || "", rfid_card_id: row.rfid_card_id || "", password: "grubpe123" });
          success++;
        } catch { errors++; }
      }
      toast.success(`Imported ${success} users. ${errors} errors.`);
      load();
    } catch (e) { toast.error(formatErr(e)); }
    if (bulkRef.current) bulkRef.current.value = "";
  };

  return (
    <AppShell variant="admin">
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-brand">Users & RFID</div>
            <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight text-ink mt-2">Everyone in the canteen</h1>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="inline-flex items-center gap-2 bg-white border border-border rounded-full px-3 py-2">
              <Search size={14} className="text-ink-500" />
              <input data-testid="user-search" value={q} onChange={e => setQ(e.target.value)} placeholder="Search by name or email" className="text-sm focus:outline-none w-56" />
            </div>
            <select data-testid="role-filter" value={filter} onChange={e => setFilter(e.target.value)} className="rounded-full border border-border bg-white px-4 py-2 text-sm font-semibold">
              <option value="all">All roles</option>
              <option value="parent">Parents</option>
              <option value="student">Students</option>
              <option value="operator">Operators</option>
              <option value="admin">Admins</option>
            </select>
            <button data-testid="add-user-btn" onClick={() => setAddUser({ name: "", email: "", role: "student", grade: "", phone: "", rfid_card_id: "", student_id: "", parent_id: "", children_ids: [] })}
              className="inline-flex items-center gap-1 px-4 py-2 rounded-full bg-brand text-white text-sm font-semibold hover:bg-brand-600">
              <Plus size={14} /> Add user
            </button>
            <button data-testid="bulk-upload-btn" onClick={() => bulkRef.current?.click()}
              className="inline-flex items-center gap-1 px-4 py-2 rounded-full bg-ink text-white text-sm font-semibold hover:bg-ink-700">
              <Upload size={14} /> Bulk upload
            </button>
            <input ref={bulkRef} type="file" accept=".csv" onChange={handleBulkUpload} className="hidden" />
          </div>
        </div>

        <div className="bg-white rounded-xl border border-border overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-cream-200 text-ink-700 text-left">
              <tr>
                <th className="p-3 font-bold uppercase text-xs tracking-wider">Name</th>
                <th className="p-3 font-bold uppercase text-xs tracking-wider">Email</th>
                <th className="p-3 font-bold uppercase text-xs tracking-wider">Role</th>
                <th className="p-3 font-bold uppercase text-xs tracking-wider">Student ID</th>
                <th className="p-3 font-bold uppercase text-xs tracking-wider">Grade</th>
                <th className="p-3 font-bold uppercase text-xs tracking-wider">Wallet</th>
                <th className="p-3 font-bold uppercase text-xs tracking-wider">RFID</th>
                <th className="p-3"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(u => (
                <tr key={u.id} className="border-t border-border hover:bg-cream-100" data-testid={`user-row-${u.id}`}>
                  <td className="p-3 font-semibold text-ink">{u.name}</td>
                  <td className="p-3 text-ink-600">{u.email}</td>
                  <td className="p-3"><span className="px-2 py-0.5 rounded-full bg-cream-200 text-xs font-bold uppercase">{u.role}</span></td>
                  <td className="p-3 font-mono text-xs text-ink-700">{u.role === "student" ? (u.student_id || "—") : "—"}</td>
                  <td className="p-3">{u.grade || "—"}</td>
                  <td className="p-3 font-bold">{inr(u.wallet_balance)}</td>
                  <td className="p-3 font-mono text-xs">{u.rfid_card_id || "—"}</td>
                  <td className="p-3 text-right">
                    <button
                      data-testid={`edit-user-${u.id}`}
                      onClick={() => setEditing({ ...u })}
                      className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-ink text-white text-xs font-semibold hover:bg-ink-700"
                    >
                      <Edit3 size={12} /> Edit
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {editing && (
        <div className="fixed inset-0 bg-ink/40 z-50 flex items-center justify-center px-4" onClick={() => setEditing(null)}>
          <div className="bg-white rounded-3xl p-7 w-full max-w-md shadow-floating max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <div className="font-heading text-xl font-bold text-ink">Edit {editing.name}</div>
              <button onClick={() => setEditing(null)} className="p-1 rounded-full hover:bg-cream-200"><X size={16} /></button>
            </div>
            <div className="space-y-3">
              <input placeholder="Name" value={editing.name || ""} onChange={e => setEditing({...editing, name: e.target.value})} className="w-full rounded-xl border border-border px-4 py-2.5 text-sm focus:border-brand focus:outline-none" />
              <input placeholder="Email" value={editing.email || ""} disabled className="w-full rounded-xl border border-border px-4 py-2.5 text-sm bg-cream-100 text-ink-500" />
              <div className="text-[10px] font-bold uppercase tracking-wider text-ink-500">Role: {editing.role}</div>
              
              {/* Student-specific fields */}
              {editing.role === "student" && (
                <>
                  <div className="grid grid-cols-2 gap-2">
                    <input placeholder="Student ID" value={editing.student_id || ""} onChange={e => setEditing({...editing, student_id: e.target.value})} className="rounded-xl border border-border px-4 py-2.5 text-sm font-mono focus:border-brand focus:outline-none" />
                    <input placeholder="Grade" value={editing.grade || ""} onChange={e => setEditing({...editing, grade: e.target.value})} className="rounded-xl border border-border px-4 py-2.5 text-sm focus:border-brand focus:outline-none" />
                  </div>
                  <input placeholder="Phone" value={editing.phone || ""} onChange={e => setEditing({...editing, phone: e.target.value})} className="w-full rounded-xl border border-border px-4 py-2.5 text-sm focus:border-brand focus:outline-none" />
                  <input placeholder="RFID Card ID" value={editing.rfid_card_id || ""} onChange={e => setEditing({...editing, rfid_card_id: e.target.value})} className="w-full rounded-xl border border-border px-4 py-2.5 text-sm font-mono focus:border-brand focus:outline-none" />
                  {/* Parent selection */}
                  <div>
                    <label className="text-[10px] font-bold uppercase tracking-wider text-ink-500 block mb-1">Link to Parent</label>
                    <select value={editing.parent_id || ""} onChange={e => setEditing({...editing, parent_id: e.target.value})} className="w-full rounded-xl border border-border px-4 py-2.5 text-sm bg-white">
                      <option value="">-- No parent linked --</option>
                      {parents.map(p => <option key={p.id} value={p.id}>{p.name} ({p.email})</option>)}
                    </select>
                  </div>
                </>
              )}
              
              {/* Parent-specific fields */}
              {editing.role === "parent" && (
                <>
                  <input placeholder="Phone" value={editing.phone || ""} onChange={e => setEditing({...editing, phone: e.target.value})} className="w-full rounded-xl border border-border px-4 py-2.5 text-sm focus:border-brand focus:outline-none" />
                  <div>
                    <label className="text-[10px] font-bold uppercase tracking-wider text-ink-500 block mb-1">Linked Children ({(editing.children_ids || []).length} selected)</label>
                    <div data-testid="edit-children-list" className="w-full rounded-xl border border-border p-2 bg-white max-h-56 overflow-y-auto space-y-1">
                      {students.length === 0 && <div className="text-xs text-ink-400 px-2 py-1">No students available.</div>}
                      {students.map(s => {
                        const checked = (editing.children_ids || []).includes(s.id);
                        return (
                          <label key={s.id} className={`flex items-center gap-2 px-2 py-1.5 rounded-lg cursor-pointer hover:bg-cream-100 ${checked ? "bg-cream-100" : ""}`}>
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={e => {
                                const cur = editing.children_ids || [];
                                const next = e.target.checked ? [...new Set([...cur, s.id])] : cur.filter(id => id !== s.id);
                                setEditing({...editing, children_ids: next});
                              }}
                              className="accent-brand"
                            />
                            <span className="text-sm text-ink">{s.name}</span>
                            <span className="text-xs text-ink-500">({s.grade || "No grade"}) — {s.student_id || s.email}</span>
                          </label>
                        );
                      })}
                    </div>
                    <div className="text-[10px] text-ink-400 mt-1">Tick the boxes to link/unlink children.</div>
                  </div>
                </>
              )}
              
              {/* Admin fields */}
              {(editing.role === "admin" || editing.role === "canteen_admin") && (
                <input placeholder="Phone" value={editing.phone || ""} onChange={e => setEditing({...editing, phone: e.target.value})} className="w-full rounded-xl border border-border px-4 py-2.5 text-sm focus:border-brand focus:outline-none" />
              )}
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button onClick={() => setEditing(null)} className="px-5 py-2.5 rounded-full text-ink-700 font-semibold hover:bg-cream-200">Cancel</button>
              <button data-testid="save-edit-user" onClick={saveEditUser} className="px-5 py-2.5 rounded-full bg-brand text-white font-semibold hover:bg-brand-600">Save</button>
            </div>
          </div>
        </div>
      )}
      {addUser && (
        <div className="fixed inset-0 bg-ink/40 z-50 flex items-center justify-center px-4" onClick={() => setAddUser(null)}>
          <div className="bg-white rounded-3xl p-7 w-full max-w-md shadow-floating max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <div className="font-heading text-xl font-bold text-ink">Add user</div>
              <button onClick={() => setAddUser(null)} className="p-1 rounded-full hover:bg-cream-200"><X size={16} /></button>
            </div>
            <div className="space-y-3">
              <input placeholder="Name *" value={addUser.name} onChange={e => setAddUser({...addUser, name: e.target.value})} className="w-full rounded-xl border border-border px-4 py-2.5 text-sm focus:border-brand focus:outline-none" />
              <input placeholder="Email *" value={addUser.email} onChange={e => setAddUser({...addUser, email: e.target.value})} className="w-full rounded-xl border border-border px-4 py-2.5 text-sm focus:border-brand focus:outline-none" />
              <select value={addUser.role} onChange={e => setAddUser({...addUser, role: e.target.value, parent_id: "", children_ids: []})} className="w-full rounded-xl border border-border px-4 py-2.5 text-sm bg-white">
                <option value="student">Student</option>
                <option value="parent">Parent</option>
                <option value="admin">Admin</option>
                <option value="canteen_admin">Canteen Admin</option>
              </select>
              
              {/* Student-specific fields */}
              {addUser.role === "student" && (
                <>
                  <input placeholder="Student ID (e.g., STU001)" value={addUser.student_id || ""} onChange={e => setAddUser({...addUser, student_id: e.target.value})} className="w-full rounded-xl border border-border px-4 py-2.5 text-sm focus:border-brand focus:outline-none font-mono" />
                  <div className="grid grid-cols-2 gap-2">
                    <input placeholder="Grade" value={addUser.grade} onChange={e => setAddUser({...addUser, grade: e.target.value})} className="rounded-xl border border-border px-4 py-2.5 text-sm focus:border-brand focus:outline-none" />
                    <input placeholder="Phone" value={addUser.phone} onChange={e => setAddUser({...addUser, phone: e.target.value})} className="rounded-xl border border-border px-4 py-2.5 text-sm focus:border-brand focus:outline-none" />
                  </div>
                  <input placeholder="RFID Card ID" value={addUser.rfid_card_id} onChange={e => setAddUser({...addUser, rfid_card_id: e.target.value})} className="w-full rounded-xl border border-border px-4 py-2.5 text-sm font-mono focus:border-brand focus:outline-none" />
                  {/* Parent selection for student */}
                  <div>
                    <label className="text-[10px] font-bold uppercase tracking-wider text-ink-500 block mb-1">Link to Parent (optional)</label>
                    <select value={addUser.parent_id || ""} onChange={e => setAddUser({...addUser, parent_id: e.target.value})} className="w-full rounded-xl border border-border px-4 py-2.5 text-sm bg-white">
                      <option value="">-- No parent linked --</option>
                      {parents.map(p => <option key={p.id} value={p.id}>{p.name} ({p.email})</option>)}
                    </select>
                  </div>
                </>
              )}
              
              {/* Parent-specific fields */}
              {addUser.role === "parent" && (
                <>
                  <input placeholder="Phone" value={addUser.phone} onChange={e => setAddUser({...addUser, phone: e.target.value})} className="w-full rounded-xl border border-border px-4 py-2.5 text-sm focus:border-brand focus:outline-none" />
                  {/* Children selection for parent */}
                  <div>
                    <label className="text-[10px] font-bold uppercase tracking-wider text-ink-500 block mb-1">Link to Children ({(addUser.children_ids || []).length} selected)</label>
                    <div data-testid="add-children-list" className="w-full rounded-xl border border-border p-2 bg-white max-h-48 overflow-y-auto space-y-1">
                      {students.length === 0 && <div className="text-xs text-ink-400 px-2 py-1">No students available.</div>}
                      {students.map(s => {
                        const checked = (addUser.children_ids || []).includes(s.id);
                        return (
                          <label key={s.id} className={`flex items-center gap-2 px-2 py-1.5 rounded-lg cursor-pointer hover:bg-cream-100 ${checked ? "bg-cream-100" : ""}`}>
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={e => {
                                const cur = addUser.children_ids || [];
                                const next = e.target.checked ? [...new Set([...cur, s.id])] : cur.filter(id => id !== s.id);
                                setAddUser({...addUser, children_ids: next});
                              }}
                              className="accent-brand"
                            />
                            <span className="text-sm text-ink">{s.name}</span>
                            <span className="text-xs text-ink-500">({s.grade || "No grade"}) — {s.student_id || s.email}</span>
                          </label>
                        );
                      })}
                    </div>
                    <div className="text-[10px] text-ink-400 mt-1">Tick the boxes to link children to this parent.</div>
                  </div>
                </>
              )}
              
              {/* Admin fields */}
              {(addUser.role === "admin" || addUser.role === "canteen_admin") && (
                <input placeholder="Phone" value={addUser.phone} onChange={e => setAddUser({...addUser, phone: e.target.value})} className="w-full rounded-xl border border-border px-4 py-2.5 text-sm focus:border-brand focus:outline-none" />
              )}
              
              <div className="text-[10px] text-ink-400">Default password: grubpe123</div>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button onClick={() => setAddUser(null)} className="px-5 py-2.5 rounded-full text-ink-700 font-semibold hover:bg-cream-200">Cancel</button>
              <button data-testid="save-new-user" onClick={saveNewUser} className="px-5 py-2.5 rounded-full bg-brand text-white font-semibold hover:bg-brand-600">Save</button>
            </div>
          </div>
        </div>
      )}
    </AppShell>
  );
}
